import type { PresentationContext, SceneContent, ScriptContent } from '../../presentation/types';
import { products, spokenList } from '../../presentation/catalogue';
import { ecosystemPhases } from '../../components/ecosystemMap';

/** Narration is assembled from whatever is enabled, so it never names a product that is not shown. */
function ecosystemScript(context: PresentationContext): ScriptContent {
  const phases = ecosystemPhases(context);
  const narration = ['Here is the wider picture.'];
  const notes = ['Moderta at the centre, empty orbit. "Here is the wider picture."'];

  phases.forEach((phase) => {
    const tier = phase[0].tier;
    if (tier === 'flagship') {
      const branches = context.products.filter((p) => p.tier === 'specialisation').map((p) => p.name);
      narration.push(`At the centre is MINT, the intelligence foundation${branches.length ? `, already specialised as ${spokenList(branches)}` : ''}.`);
      notes.push('MINT arrives first and largest. Do not re-explain it; point at its specialisations.');
    } else if (tier === 'selected') {
      narration.push(...phase.map((g) => products[g.lead].pitch));
      notes.push('Selected work appears quietly. One sentence.');
    } else {
      const lead = context.isEnabled('mint') ? 'Beyond MINT, Moderta builds other platforms and products.' : 'Moderta builds platforms and products around different problems.';
      narration.push(`${lead} ${phase.map((g) => products[g.lead].pitch).join(' ')}`);
      notes.push(`${spokenList(phase.map((g) => products[g.lead].name))}. One sentence each.`);
    }
  });

  narration.push('Different problems, approached with the same connected way of thinking.');
  notes.push('Connections light up. "Different problems. Connected thinking."');

  return {
    title: 'The Moderta Ecosystem',
    onScreen: ['MODERTA', ...context.groups.map((g) => products[g.lead].name), 'DIFFERENT PROBLEMS', 'CONNECTED THINKING.'],
    visual: `An empty orbit around Moderta. ${context.isEnabled('mint') ? 'MINT arrives first and largest, carrying its specialisations; independent' : 'Independent'} platforms and products follow along their own spokes, each with its own motif; selected work arrives last and smaller. The orbit lights up, energy pulses outward, and the statement takes the centre.`,
    narration,
    presenterNotes: notes,
  };
}

export const ecosystem: SceneContent = {
  id: 'ecosystem',
  shortLabel: 'ECOSYSTEM',
  title: 'The Moderta Ecosystem',
  seconds: 50,
  brand: 'moderta',
  bridgeIn: (context) => (context.isEnabled('mint') ? 'Step back, and MINT becomes one part of a wider ecosystem.' : 'Step back and look at the wider Moderta ecosystem.'),
  atmosphere: (step) => ({ pattern: step >= 2 ? 'inward' : 'orbit', density: 0.26, opacity: 0.07, cameraZ: 15 }),
  script: ecosystemScript,
};
